"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Clock, UserCheck, Sparkles, Search, MessageSquare, Lock, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
const reassuringPoints = [{
  icon: UserCheck,
  title: "100% Direct Consultant Access",
  description: "You work directly with me, Yassine Essaadi. No junior account managers, middle-men, or outsourced work."
}, {
  icon: ShieldCheck,
  title: "Honest, Practical Recommendations",
  description: "I evaluate your website and provide practical, high-impact SEO strategies tailored to your exact business goals."
}, {
  icon: Clock,
  title: "Fast 24-Hour Response",
  description: "Submit your details and I will personally review your site before our introductory call."
}];
const guaranteesList = ["Zero high-pressure sales tactics", "Custom SEO roadmap outline", "Clear deliverables and timelines", "Transparent project scope"];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="p-8 text-center bg-card rounded-2xl border border-border shadow-md space-y-4">
        <div className="w-14 h-14 mx-auto rounded-full bg-accent/20 flex items-center justify-center text-accent">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold font-serif text-foreground">
          Consultation Request Received
        </h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          {successMessage || "Thank you! I have received your request and will review your website personally before contacting you within 24 hours."}
        </p>
        <div className="pt-2">
          <Button onClick={resetForm} variant="outline" className="rounded-lg hover:bg-accent/10 border-border text-foreground">
            Submit Another Request
          </Button>
        </div>
      </motion.div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Please enter your full name"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Sarah Jenkins" className="bg-background border-input focus-visible:ring-primary rounded-lg h-11" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Business email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Business Email</FormLabel>
            <FormControl>
              <Input type="email" placeholder="sarah@yourcompany.com" className="bg-background border-input focus-visible:ring-primary rounded-lg h-11" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: "Website URL is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Website URL</FormLabel>
            <FormControl>
              <Input type="url" placeholder="https://yourcompany.com" className="bg-background border-input focus-visible:ring-primary rounded-lg h-11" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Key SEO Focus or Question (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Technical audit, National ranking, or Lead generation" className="bg-background border-input focus-visible:ring-primary rounded-lg h-11" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 shadow-md transition-all cursor-pointer mt-2">
        {isSubmitting ? <span className="flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 animate-spin" />
            Preparing Your Consultation...
          </span> : <span className="flex items-center justify-center gap-2">
            Book My Free SEO Consultation
            <ArrowRight className="w-5 h-5" />
          </span>}
      </Button>

      <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5 pt-2">
        <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        Your privacy is strictly protected. No spam, ever.
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationcta" data-nav="light" className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div className="max-w-6xl mx-auto" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }}>
          <motion.div variants={itemVariants} className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4 border border-border">
              <Sparkles className="w-3.5 h-3.5 text-accent" />
              <span>Direct Expert Engagement</span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight leading-tight">
              Get Your Bedford Business Found on Google
            </h2>
            
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
              Book a direct, 30-minute consultation with me today. I will review your Bedford website, uncover your biggest search growth opportunities, and provide a clear roadmap to secure consistent local leads.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <motion.div variants={itemVariants} className="lg:col-span-6 space-y-8">
              <div className="space-y-6">
                <div className="p-6 rounded-2xl bg-card border border-border shadow-xs">
                  <div className="flex items-start gap-4">
                    <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Senior SEO Consultant" className="w-16 h-16 rounded-full object-cover border-2 border-primary shrink-0 shadow-sm" loading="lazy" />
                    <div>
                      <h3 className="text-lg font-bold text-foreground font-serif">
                        You Work Directly With Me
                      </h3>
                      <p className="text-sm text-primary font-medium mb-1">
                        Yassine Essaadi — Independent Freelance SEO Consultant
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        "I personally lead every strategy, audit, and execution plan. When you reach out, you get my direct expertise—never an account manager or junior staff member."
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  {reassuringPoints.map((point, index) => {
                  const IconComponent = point.icon;
                  return <div key={point.title} data-index={index} className="flex items-start gap-4 p-4 rounded-xl bg-background border border-border shadow-2xs transition-all hover:border-primary/30">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                          <IconComponent className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="text-base font-semibold text-foreground">
                            {point.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-0.5 leading-normal">
                            {point.description}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                <div className="p-5 rounded-xl bg-muted/60 border border-border">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground/80 mb-3 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-primary" />
                    My Consultation Guarantees
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {guaranteesList.map((item, idx) => <div key={item} data-index={idx} className="flex items-center gap-2 text-xs font-medium text-foreground">
                        <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                        <span>{item}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link to="/ContactPage" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto h-11 px-5 rounded-lg border-primary text-primary hover:bg-primary/5 font-semibold">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Send a Quick Message
                  </Button>
                </Link>
                <Link to="/SeoAuditsPage" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto h-11 px-5 rounded-lg border-border text-foreground hover:bg-muted font-semibold">
                    <FileText className="w-4 h-4 mr-2 text-primary" />
                    Get a Free SEO Audit
                  </Button>
                </Link>
                <Link to="/CaseStudiesPage" className="w-full sm:w-auto">
                  <Button variant="ghost" className="w-full sm:w-auto h-11 px-5 rounded-lg text-foreground hover:bg-muted font-semibold">
                    <Search className="w-4 h-4 mr-2 text-accent" />
                    Explore Case Studies
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="lg:col-span-6">
              <Card className="border-border shadow-xl bg-card rounded-2xl overflow-hidden p-0">
                <div className="bg-primary px-6 py-5 text-primary-foreground border-b border-primary/20">
                  <div className="flex items-center gap-2 mb-1">
                    <SafeHydrate><Calendar className="w-5 h-5 text-accent" /></SafeHydrate>
                    <h3 className="text-xl font-bold font-serif">
                      Schedule Your Free Strategy Session
                    </h3>
                  </div>
                  <p className="text-xs text-primary-foreground/80">
                    Fill in your details below to request a direct SEO consultation with Yassine.
                  </p>
                </div>

                <CardContent className="p-6 sm:p-8">
                  <FormProvider formId="consultation-cta-form" sectionName="consultation-cta" defaultValues={{
                  name: "",
                  email: "",
                  website: "",
                  message: ""
                }} validationRules={{
                  name: {
                    required: true,
                    minLength: 2
                  },
                  email: {
                    required: true,
                    pattern: "email"
                  },
                  website: {
                    required: true
                  }
                }} submitText="Book My Free SEO Consultation" successMessage="Thank you! I have received your request and will review your website personally before contacting you within 24 hours.">
                    <ConsultationFormInner />
                  </FormProvider>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}